<template>
  <section v-bind="$attrs" class="rounded-xl bg-gray-100 pb-8 ps-8 pt-8">
    <div class="mb-8 flex items-center justify-between pe-12 ps-4">
      <div class="text-subject font-bold text-dark">
        {{ `${$t('search.nonCommercial')} ${$t('results')}` }}
      </div>

      <JButton
        v-if="
          !isLoadingSearchAndFilter &&
          !airCraftsSearchResultsStore.isSearchEmpty
        "
        type="button"
        size="xs"
        color="secondary"
        icon="filter"
        variant="tonal"
        @click="!airCraftsSearchResultsStore.isLoading && setFilterDialog(true)"
      />
    </div>

    <div
      v-if="
        !isLoadingSearchAndFilter &&
        airCraftsSearchResultsStore.totalAirCraftsCount > 0
      "
      class="my-12 flex items-center justify-between pe-12 ps-4"
    >
      <span class="font-bold">{{ $t('search.chooseYourFlight') }}</span>
      <span class="font-medium text-gray-600">
        {{
          $t('search.nResults', {
            count: airCraftsSearchResultsStore.totalAirCraftsCount,
          })
        }}
      </span>
    </div>

    <!-- CAROUSEL -->
    <div ref="swiperElementRef" class="swiper">
      <div class="swiper-wrapper">
        <template
          v-if="
            airCraftsSearchResultsStore.airCraftsList.length ||
            isLoadingSearchAndFilter
          "
        >
          <template v-if="isLoadingSearchAndFilter">
            <div v-for="i in 4" :key="i" class="swiper-slide">
              <AirCraftCardSkeleton />
            </div>
          </template>

          <template v-else>
            <div
              v-for="(
                airCraft, index
              ) in airCraftsSearchResultsStore.airCraftsList"
              :key="index"
              class="swiper-slide"
            >
              <AirCraftCard
                :imageSrc="`/jets/jet-${index % 4}.png`"
                :airCraft="airCraft"
                :isBestPrice="index === 0"
                @select="onSelect(airCraft)"
                @show-detail="showAirCraftDetail(airCraft)"
              />
            </div>

            <template
              v-if="
                airCraftsSearchResultsStore.airCraftsList.length !==
                airCraftsSearchResultsStore.totalAirCraftsCount
              "
            >
              <div
                v-for="i in MINIMUM_RESULT_SIZE"
                :key="i"
                class="swiper-slide"
              >
                <AirCraftCardSkeleton class="h-full" />
              </div>
            </template>
          </template>
        </template>

        <!-- NO RESULT -->
        <template v-else>
          <NoSearchResult
            :image="{ src: '/airplane.svg', alt: 'AirCraft' }"
            :description="
              $t('search.noResultFound', { title: $t('search.airCraft') })
            "
            class="w-full"
          />
        </template>
      </div>
    </div>
  </section>

  <!-- FILTER -->
  <JBottomSheet v-model="filterDialogIsOpen" :eager="false">
    <template #header>
      <JContainer class="px-16 pt-24 text-pane-header font-bold text-dark">
        {{ $t('search.airCraft') }} {{ $t('words.filters') }}
      </JContainer>
    </template>

    <div class="p-24">
      <AirCraftsResultsFilter @submit="onSubmitForm" />
    </div>
  </JBottomSheet>

  <!-- DETAIL -->
  <JBottomSheet v-model="airCraftDetailIsOpen" fullScreen :eager="false">
    <template #header>
      <JContainer
        class="absolute left-0 top-0 z-10 flex w-full justify-center px-16"
      >
        <TheMobileHeader
          titleClass="text-dark font-bold text-base"
          showBack
          @back="setAirCraftDetailIsOpen(false)"
        />
      </JContainer>
    </template>

    <ServerError v-if="isError" />

    <AirCraftDetail
      v-else
      :isLoading="airCraftInfoIsFetching"
      @select="findAirCraftAndAddToItinerarySummary"
      @retry="refetch"
    />
  </JBottomSheet>
</template>

<script setup lang="ts">
import Swiper, { type Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import { useToggle } from '@vueuse/core'
import {
  computed,
  defineAsyncComponent,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { JBottomSheet, JButton, JContainer } from '@journey/ui-kit'
import type { Aircraft } from '@/modules/Search/types/model/search'
import { useAirCraftInfoQuery } from '@/modules/Search/requests/queries'
import { useFilteredAirCraftsListMutation } from '@/modules/Search/requests/mutations'
import {
  DEFAULT_FLIGHT_PAGE_SIZE,
  MINIMUM_RESULT_SIZE,
} from '@/modules/Search/constants'
import useSearchPanelHistoryStore from '@/modules/Search/store/searchPanelHistory'
import AirCraftCard from '@/modules/Core/components/shared/AirCraftCard/AirCraftCard.vue'
import useAirCraftsSearchResultsStore from '@/modules/Search/store/aircraftSearchResults'
import useAirCraftSearchFiltersStore from '@/modules/Search/store/airCraftSearchFilters'
import type { FilteredAirCraftListPayload } from '@/modules/Search/types/api/payload/filteredAirCraftList'
import AirCraftCardSkeleton from '@/modules/Core/components/shared/AirCraftCard/AirCraftCardSkeleton/AirCraftCardSkeleton.vue'
import useBookingAircraftStore from '@/modules/Book/store/bookingAirCraft'

defineOptions({
  inheritAttrs: false,
})

const AirCraftDetail = defineAsyncComponent(
  () =>
    import('@/modules/Core/components/shared/AirCraftDetail/AirCraftDetail.vue')
)
const ServerError = defineAsyncComponent(
  () => import('@/modules/Core/components/shared/ServerError/ServerError.vue')
)
const TheMobileHeader = defineAsyncComponent(
  () =>
    import('@/modules/Core/components/layout/MobileHeader/TheMobileHeader.vue')
)
const NoSearchResult = defineAsyncComponent(
  () => import('@/modules/Search/components/NoSearchResult/NoSearchResult.vue')
)
const AirCraftsResultsFilter = defineAsyncComponent(
  () =>
    import(
      '@/modules/Search/components/AirCraftsResults/AirCraftsResultsFilter/AirCraftsResultsFilter.vue'
    )
)

const swiperRef = ref<SwiperType | null>(null)
const swiperElementRef = ref<HTMLElement | null>(null)

// FILTERS
const apiPage = ref(1)
const size = ref(DEFAULT_FLIGHT_PAGE_SIZE)
const query = ref('')

const airCraftsSearchResultsStore = useAirCraftsSearchResultsStore()

onMounted(() => {
  swiperRef.value = new Swiper(swiperElementRef.value!, {
    slidesPerView: 1.3,
    spaceBetween: 10,
    on: {
      slideChange: onSlideChange,
    },
  })
})

const searchPanelHistoryStore = useSearchPanelHistoryStore()

const [filterDialogIsOpen, setFilterDialog] = useToggle(false)
const [airCraftDetailIsOpen, setAirCraftDetailIsOpen] = useToggle(false)

const searchId = computed(
  () => searchPanelHistoryStore.history.searchId as string
)
const customerCode = ref(import.meta.env.VITE_CUSTOMER_CODE)
const searchOption = ref(-1)

const bookingAirCraftStore = useBookingAircraftStore()
function onSelect(airCraft: Aircraft) {
  airCraftsSearchResultsStore.setSelectAirCraftToItinerary(airCraft.id)
  bookingAirCraftStore.setIsOpenBookingAircraft(true)
}

const {
  data: airCraftDetail,
  isFetching: airCraftInfoIsFetching,
  isError,
  refetch,
} = useAirCraftInfoQuery(searchId, searchOption, customerCode, {
  enabled: airCraftDetailIsOpen,
})

function showAirCraftDetail(payload: Aircraft) {
  searchOption.value = payload.searchOption

  setAirCraftDetailIsOpen(true)
}

watch(airCraftDetail, (value) => {
  if (!value) {
    return
  }

  if (!value.data) {
    return
  }

  airCraftsSearchResultsStore.setSelectedAirCraft(value.data)
})

function findAirCraftAndAddToItinerarySummary(id: string) {
  const airCraft = airCraftsSearchResultsStore.findAirCraftById(id)

  if (!airCraft) {
    return
  }

  airCraftsSearchResultsStore.setSelectAirCraftToItinerary(id)
  bookingAirCraftStore.setIsOpenBookingAircraft(true)
}

const { mutateAsync } = useFilteredAirCraftsListMutation({
  meta: {
    noSuccessToast: true,
  },
})

const filteredAirCraftsStore = useAirCraftSearchFiltersStore()

const apiPayload = reactive<FilteredAirCraftListPayload>({
  searchId: '',
  isTwinEngine: filteredAirCraftsStore.transformedFilters.isTwinEngine,
  hasFlightAttendantService:
    filteredAirCraftsStore.transformedFilters.hasFlightAttendantService,
  hasAirCondition: filteredAirCraftsStore.transformedFilters.hasAirCondition,
  hasLavatory: filteredAirCraftsStore.transformedFilters.hasLavatory,
  isPetAllowed: filteredAirCraftsStore.transformedFilters.isPetAllowed,
  isDaylightRestricted:
    filteredAirCraftsStore.transformedFilters.isDaylightRestricted,
  cateringType: filteredAirCraftsStore.transformedFilters.cateringType,
  query: query.value,
  page: apiPage.value,
  size: size.value,
  sortOrder: 'ascending',
})

async function onSubmitFilters() {
  apiPayload.searchId = searchId.value
  const { data } = await mutateAsync(apiPayload)

  if (!data) {
    return
  }

  const { items, page } = data.data

  airCraftsSearchResultsStore.appendAirCrafts(items)

  if (page.items !== airCraftsSearchResultsStore.totalAirCraftsCount) {
    airCraftsSearchResultsStore.setTotalAirCraftsCount(page.items ?? 1)
  }
}

const [isLoadingFilter, toggleIsFilterLoading] = useToggle()

async function onSubmitForm() {
  toggleIsFilterLoading(true)

  apiPage.value = 1
  Object.assign(apiPayload, {
    ...filteredAirCraftsStore.transformedFilters,
    page: 1,
  })

  setFilterDialog(false)

  airCraftsSearchResultsStore.resetAirCrafts()

  await onSubmitFilters()

  toggleIsFilterLoading(false)
}

watch(isLoadingFilter, filteredAirCraftsStore.setLoading)

const isLoadingSearchAndFilter = computed(
  () => airCraftsSearchResultsStore.isLoading || isLoadingFilter.value
)

async function onSlideChange() {
  const activeIndex = swiperRef.value?.activeIndex
  if (activeIndex === undefined) {
    return
  }

  if (
    airCraftsSearchResultsStore.airCraftsList.length ===
    airCraftsSearchResultsStore.totalAirCraftsCount
  ) {
    return
  }

  if ((activeIndex + 1) % (DEFAULT_FLIGHT_PAGE_SIZE - 4) !== 0) {
    return
  }

  if (
    Math.ceil((activeIndex + 1) / DEFAULT_FLIGHT_PAGE_SIZE) < apiPage.value &&
    apiPage.value !== 1
  ) {
    return
  }

  swiperRef.value?.update()

  await getFetchOrScrollFlightsStatus()

  swiperRef.value?.update()
}

async function getFetchOrScrollFlightsStatus() {
  if (isLoadingFilter.value) {
    return
  }

  apiPage.value++
  apiPayload.page = apiPage.value
  await onSubmitFilters()
}

async function onSelectCity() {
  toggleIsFilterLoading(true)

  apiPage.value = 1
  apiPayload.page = apiPage.value

  airCraftsSearchResultsStore.resetAirCrafts()

  await onSubmitFilters()

  toggleIsFilterLoading(false)
}

watch(
  () => airCraftsSearchResultsStore.isLoading,
  () => {
    apiPage.value = 1
  }
)

onBeforeUnmount(() => {
  swiperRef.value?.destroy()
})

defineExpose({
  onSelectCity,
})
</script>

<style lang="postcss" scoped>
.swiper {
  @apply z-0;
}
</style>
